import {
  TAX_OBLIGATION_STATUS_LABELS,
  type TaxObligation,
  taxObligationStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateInput,
  Dialog,
  EmptyState,
  FormField,
  Input,
  MoneyInput,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  FileChartColumn,
  Plus,
  RefreshCw,
  Settings2,
} from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { Amount, todayIstanbul } from "../../../features/accounting/shared.js";
import { ExpensesPage } from "../../../features/expenses/shared.js";
import {
  buildCalendarMonth,
  MONTH_NAMES,
  monthRange,
  shiftMonth,
  WEEKDAY_NAMES,
} from "../../../features/tax-calendar/calendar-grid.js";
import {
  DaysLeftBadge,
  ObligationStatusBadge,
  useRefreshTaxCalendar,
  useTaxCalendarPermissions,
  useTaxObligations,
} from "../../../features/tax-calendar/shared.js";
import { useBankAccounts } from "../../../features/treasury/definitions.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

const searchSchema = z.object({
  ...companySearchShape,
  view: z.enum(["calendar", "list"]).optional().catch(undefined),
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional()
    .catch(undefined),
  status: taxObligationStatusSchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/vergi-takvimi/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: TaxCalendarPage,
});

function TaxCalendarPage(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const permissions = useTaxCalendarPermissions();
  const view = search.view ?? "calendar";
  const today = todayIstanbul();
  const anchor = search.month ?? today.slice(0, 7);
  const year = Number(anchor.slice(0, 4));
  const monthIndex = Number(anchor.slice(5, 7)) - 1;

  return (
    <ExpensesPage
      title={t("menu.taxCalendar")}
      description={t("taxCalendar.description")}
      breadcrumb={[{ label: t("menu.taxCalendar") }]}
      actions={({ companyId }) => (
        <>
          <Button variant="outline" asChild>
            <Link to="/vergi-takvimi/kdv-ozeti">
              <FileChartColumn aria-hidden="true" />
              {t("taxCalendar.vatSummary.title")}
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/vergi-takvimi/kurallar">
              <Settings2 aria-hidden="true" />
              {t("taxCalendar.rules.title")}
            </Link>
          </Button>
          {permissions.canManage ? <GenerateButton companyId={companyId} /> : null}
        </>
      )}
    >
      {({ companyId }) => (
        <div className="grid gap-4">
          <Tabs
            value={view}
            onValueChange={(next) =>
              void navigate({
                search: (previous) => ({ ...previous, view: next as "calendar" | "list" }),
                replace: true,
              })
            }
          >
            <TabsList aria-label={t("taxCalendar.viewTabs")}>
              <TabsTrigger value="calendar">{t("taxCalendar.calendarView")}</TabsTrigger>
              <TabsTrigger value="list">{t("taxCalendar.listView")}</TabsTrigger>
            </TabsList>
            <TabsContent value="calendar">
              {view === "calendar" ? (
                <MonthCalendar
                  companyId={companyId}
                  year={year}
                  monthIndex={monthIndex}
                  onMonth={(next) =>
                    void navigate({
                      search: (previous) => ({
                        ...previous,
                        month: `${String(next.year).padStart(4, "0")}-${String(next.monthIndex + 1).padStart(2, "0")}`,
                      }),
                      replace: true,
                    })
                  }
                />
              ) : null}
            </TabsContent>
            <TabsContent value="list">
              {view === "list" ? (
                <ObligationList
                  companyId={companyId}
                  status={search.status}
                  onStatus={(status) =>
                    void navigate({ search: (previous) => ({ ...previous, status }), replace: true })
                  }
                />
              ) : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </ExpensesPage>
  );
}

function GenerateButton({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTaxCalendar();
  const generate = useMutation({
    mutationFn: async () =>
      (await api.POST("/tax-obligations/generate", { body: { companyId, horizonDays: 120 } })).data!,
    // Uç hem ufuk içindeki TÜM yükümlülükleri (`items`) hem bu koşunun ÜRETTİĞİ sayıları (`result`) döndürür.
    onSuccess: async (data) => {
      await refresh();
      toast({
        tone: "success",
        title: t("taxCalendar.generated", { count: data.items.length }),
        description: t("taxCalendar.generatedDetail", {
          created: data.result.obligationsCreated,
          rules: data.result.rulesEnsured,
        }),
      });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  return (
    <Button loading={generate.isPending} onClick={() => generate.mutate()}>
      <RefreshCw aria-hidden="true" />
      {t("taxCalendar.generate")}
    </Button>
  );
}

function MonthCalendar({
  companyId,
  year,
  monthIndex,
  onMonth,
}: {
  companyId: string;
  year: number;
  monthIndex: number;
  onMonth: (next: { year: number; monthIndex: number }) => void;
}): ReactElement {
  const { t } = useTranslation();
  const range = monthRange(year, monthIndex);
  const obligations = useTaxObligations(companyId, { dueFrom: range.from, dueTo: range.to });
  const month = useMemo(
    () => buildCalendarMonth(year, monthIndex, obligations.data ?? []),
    [year, monthIndex, obligations.data],
  );
  const [selected, setSelected] = useState<TaxObligation | null>(null);

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-label={t("taxCalendar.previousMonth")}
          onClick={() => onMonth(shiftMonth(year, monthIndex, -1))}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <h2 className="text-base font-semibold">
          {MONTH_NAMES[monthIndex]} {year}
        </h2>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("taxCalendar.nextMonth")}
          onClick={() => onMonth(shiftMonth(year, monthIndex, 1))}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
      {obligations.error ? <Alert tone="danger">{errorMessage(obligations.error)}</Alert> : null}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-sm">
          <caption className="sr-only">
            {t("taxCalendar.calendarCaption", { month: MONTH_NAMES[monthIndex], year })}
          </caption>
          <thead>
            <tr>
              {WEEKDAY_NAMES.map((day) => (
                <th
                  key={day}
                  scope="col"
                  className="border px-2 py-1 text-xs font-medium text-muted-foreground"
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {month.weeks.map((week) => (
              <tr key={week[0]!.date}>
                {week.map((cell) => (
                  <td
                    key={cell.date}
                    className={`h-24 border align-top p-1 ${cell.inMonth ? "" : "bg-muted/40 text-muted-foreground"}`}
                  >
                    <span className="text-xs">{Number(cell.date.slice(8, 10))}</span>
                    <ul className="mt-1 grid gap-1">
                      {cell.obligations.map((obligation) => (
                        <li key={obligation.id}>
                          <button
                            type="button"
                            className="min-h-6 w-full truncate rounded bg-secondary px-1 py-0.5 text-left text-xs"
                            onClick={() => setSelected(obligation)}
                          >
                            {obligation.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ObligationDialog obligation={selected} companyId={companyId} onClose={() => setSelected(null)} />
    </div>
  );
}

function ObligationList({
  companyId,
  status,
  onStatus,
}: {
  companyId: string;
  status: TaxObligation["status"] | undefined;
  onStatus: (status: TaxObligation["status"] | undefined) => void;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useTaxCalendarPermissions();
  const obligations = useTaxObligations(companyId, { ...(status ? { status } : {}) });
  const [selected, setSelected] = useState<TaxObligation | null>(null);
  const [creating, setCreating] = useState(false);

  const columns = useMemo<DataTableColumn<TaxObligation>[]>(
    () => [
      {
        id: "title",
        header: t("taxCalendar.fields.title"),
        hideable: false,
        cell: (row) => (
          <button
            type="button"
            className="font-medium underline underline-offset-4"
            onClick={() => setSelected(row)}
          >
            {row.title}
          </button>
        ),
        exportValue: (row) => row.title,
      },
      { id: "periodKey", header: t("taxCalendar.fields.period"), cell: (row) => row.periodKey },
      { id: "dueDate", header: t("taxCalendar.fields.dueDate"), cell: (row) => formatDate(row.dueDate) },
      {
        id: "daysLeft",
        header: t("taxCalendar.fields.daysLeft"),
        cell: (row) => (row.status === "OPEN" ? <DaysLeftBadge daysLeft={row.daysLeft} /> : "—"),
        exportValue: (row) => row.daysLeft,
      },
      {
        id: "amount",
        header: t("taxCalendar.fields.amount"),
        align: "right",
        cell: (row) =>
          row.amount === null ? (
            <span className="text-muted-foreground">{t("taxCalendar.fields.amountUnknown")}</span>
          ) : (
            <span className="flex items-center justify-end gap-1">
              <Amount value={row.amount} />
              <StatusBadge tone={row.amountIsEstimate ? "warning" : "success"}>
                {row.amountIsEstimate ? t("taxCalendar.fields.estimate") : t("taxCalendar.fields.exact")}
              </StatusBadge>
            </span>
          ),
        exportValue: (row) => row.amount,
      },
      {
        id: "status",
        header: t("common.status"),
        hideable: false,
        cell: (row) => <ObligationStatusBadge status={row.status} />,
        exportValue: (row) => TAX_OBLIGATION_STATUS_LABELS[row.status],
      },
    ],
    [t],
  );

  return (
    <>
      <DataTable
        caption={t("taxCalendar.listCaption")}
        columns={columns}
        rows={obligations.data ?? []}
        rowKey={(row) => row.id}
        loading={obligations.isPending}
        error={obligations.error ? <Alert tone="danger">{errorMessage(obligations.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<CalendarClock />}
            title={t("taxCalendar.emptyTitle")}
            description={t("taxCalendar.emptyDescription")}
          />
        }
        toolbar={
          permissions.canManage ? (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus aria-hidden="true" />
              {t("taxCalendar.addManual")}
            </Button>
          ) : undefined
        }
        filters={
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={status ?? ""}
                onChange={(event) =>
                  onStatus(
                    event.target.value === ""
                      ? undefined
                      : taxObligationStatusSchema.parse(event.target.value),
                  )
                }
              >
                <option value="">{t("common.all")}</option>
                {taxObligationStatusSchema.options.map((option) => (
                  <option key={option} value={option}>
                    {TAX_OBLIGATION_STATUS_LABELS[option]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        }
      />
      <ObligationDialog obligation={selected} companyId={companyId} onClose={() => setSelected(null)} />
      <ManualObligationDialog open={creating} companyId={companyId} onOpenChange={setCreating} />
    </>
  );
}

/** Yükümlülük eylemleri: tutar gir, tamamlandı işaretle (isteğe bağlı bankadan ödeme), iptal. */
function ObligationDialog({
  obligation,
  companyId,
  onClose,
}: {
  obligation: TaxObligation | null;
  companyId: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTaxCalendar();
  const permissions = useTaxCalendarPermissions();
  const bankAccounts = useBankAccounts(companyId);
  const [amount, setAmount] = useState<string | null>(null);
  const [exact, setExact] = useState(false);
  const [bankAccountId, setBankAccountId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const act = useMutation({
    mutationFn: async (action: "amount" | "complete" | "cancel") => {
      const params = { path: { id: obligation!.id } };
      if (action === "amount")
        return api.PATCH("/tax-obligations/{id}", {
          params,
          body: { amount, amountIsEstimate: !exact, version: obligation!.version } as never,
        });
      if (action === "complete")
        return api.POST("/tax-obligations/{id}/complete", {
          params,
          body: {
            paidOn: undefined,
            bankAccountId: bankAccountId || null,
            amount: null,
            note: null,
            version: obligation!.version,
          } as never,
        });
      return api.POST("/tax-obligations/{id}/cancel", {
        params,
        body: { reason, version: obligation!.version },
      });
    },
    onSuccess: async (_data, action) => {
      setError(null);
      await refresh();
      toast({ tone: "success", title: t(`taxCalendar.done.${action}`) });
      if (action !== "amount") onClose();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={obligation !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={obligation?.title ?? ""}
      description={
        obligation ? t("taxCalendar.obligationDescription", { period: obligation.periodKey }) : undefined
      }
    >
      {obligation ? (
        <div className="grid gap-4">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <Card className="grid gap-1 bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("taxCalendar.fields.dueDate")}</span>
              <span>{formatDate(obligation.dueDate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("common.status")}</span>
              <ObligationStatusBadge status={obligation.status} />
            </div>
          </Card>

          {permissions.canManage && obligation.status === "OPEN" ? (
            <>
              <form
                className="grid gap-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  act.mutate("amount");
                }}
              >
                <FormField label={t("taxCalendar.fields.amount")}>
                  {(control) => <MoneyInput {...control} value={amount} onValueChange={setAmount} />}
                </FormField>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-input accent-[var(--primary)]"
                    checked={exact}
                    onChange={(event) => setExact(event.target.checked)}
                  />
                  {t("taxCalendar.fields.markExact")}
                </label>
                <Button
                  type="submit"
                  variant="outline"
                  size="sm"
                  loading={act.isPending}
                  className="justify-self-start"
                >
                  {t("taxCalendar.setAmount")}
                </Button>
              </form>

              <FormField
                label={t("taxCalendar.fields.payFromBank")}
                description={t("taxCalendar.fields.payFromBankHint")}
              >
                {(control) => (
                  <Select
                    {...control}
                    value={bankAccountId}
                    onChange={(event) => setBankAccountId(event.target.value)}
                  >
                    <option value="">{t("taxCalendar.fields.noBankPayment")}</option>
                    {(bankAccounts.data ?? []).map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>

              <FormField label={t("taxCalendar.fields.cancelReason")}>
                {(control) => (
                  <Input
                    {...control}
                    value={reason}
                    maxLength={500}
                    onChange={(event) => setReason(event.target.value)}
                  />
                )}
              </FormField>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  variant="destructive"
                  loading={act.isPending}
                  disabled={reason.trim().length < 3}
                  onClick={() => act.mutate("cancel")}
                >
                  {t("taxCalendar.cancelObligation")}
                </Button>
                <Button loading={act.isPending} onClick={() => act.mutate("complete")}>
                  {t("taxCalendar.complete")}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

function ManualObligationDialog({
  open,
  companyId,
  onOpenChange,
}: {
  open: boolean;
  companyId: string;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTaxCalendar();
  const [state, setState] = useState({
    code: "",
    title: "",
    periodKey: todayIstanbul().slice(0, 7),
    dueDate: todayIstanbul() as string | null,
    amount: null as string | null,
    note: "",
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () =>
      api.POST("/tax-obligations", {
        body: {
          companyId,
          code: state.code.trim().toUpperCase(),
          periodKey: state.periodKey.trim(),
          title: state.title.trim(),
          dueDate: state.dueDate ?? "",
          isPayment: true,
          amount: state.amount,
          amountIsEstimate: true,
          note: state.note.trim() || null,
        } as never,
      }),
    onSuccess: async () => {
      onOpenChange(false);
      await refresh();
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("taxCalendar.addManual")}
      description={t("taxCalendar.addManualDescription")}
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField
          label={t("taxCalendar.fields.code")}
          required
          description={t("taxCalendar.fields.codeHint")}
        >
          {(control) => (
            <Input
              {...control}
              required
              value={state.code}
              onChange={(event) => setState({ ...state, code: event.target.value })}
            />
          )}
        </FormField>
        <FormField label={t("taxCalendar.fields.title")} required>
          {(control) => (
            <Input
              {...control}
              required
              value={state.title}
              maxLength={200}
              onChange={(event) => setState({ ...state, title: event.target.value })}
            />
          )}
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t("taxCalendar.fields.period")}
            required
            description={t("taxCalendar.fields.periodHint")}
          >
            {(control) => (
              <Input
                {...control}
                required
                value={state.periodKey}
                onChange={(event) => setState({ ...state, periodKey: event.target.value })}
              />
            )}
          </FormField>
          <FormField label={t("taxCalendar.fields.dueDate")} required>
            {(control) => (
              <DateInput
                {...control}
                value={state.dueDate}
                onValueChange={(value) => setState({ ...state, dueDate: value })}
              />
            )}
          </FormField>
        </div>
        <FormField label={t("taxCalendar.fields.amount")}>
          {(control) => (
            <MoneyInput
              {...control}
              value={state.amount}
              onValueChange={(value) => setState({ ...state, amount: value })}
            />
          )}
        </FormField>
        <FormField label={t("taxCalendar.fields.note")}>
          {(control) => (
            <Textarea
              {...control}
              value={state.note}
              maxLength={500}
              onChange={(event) => setState({ ...state, note: event.target.value })}
            />
          )}
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={create.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
